import React from 'react';
import { AudioStats } from '../utils/audioAnalysis';
import PracticeRecommendations from './PracticeRecommendations';
import DiagnosticData from './DiagnosticData';
import './VoiceStats.css';

interface VoiceStatsProps {
  stats: AudioStats;
  audioUrl: string;
}

const VoiceStats: React.FC<VoiceStatsProps> = ({ stats, audioUrl }) => {
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };
  
  const getPitchDescription = (pitch: number): string => {
    if (pitch < 100) return 'Very Low';
    if (pitch < 150) return 'Low';
    if (pitch < 200) return 'Medium';
    if (pitch < 250) return 'High';
    return 'Very High';
  };
  
  const getVolumeDescription = (volume: number): string => {
    if (volume < 20) return 'Very Quiet';
    if (volume < 40) return 'Quiet';
    if (volume < 60) return 'Normal';
    if (volume < 80) return 'Loud';
    return 'Very Loud';
  };
  
  const getSpeakingRateDescription = (wpm: number): string => {
    if (wpm < 100) return 'Very Slow';
    if (wpm < 130) return 'Slow';
    if (wpm < 160) return 'Normal';
    if (wpm < 190) return 'Fast';
    return 'Very Fast';
  };
  
  return (
    <div className="voice-stats">
      <h2 className="stats-title">Voice Analysis Results</h2>
      
      <div className="audio-player">
        <audio controls src={audioUrl}>
          Your browser does not support the audio element.
        </audio>
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">⏱️</div>
          <div className="stat-label">Duration</div>
          <div className="stat-value">{formatDuration(stats.duration)}</div>
          <div className="stat-detail">{stats.pauseCount} pauses detected</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🎵</div>
          <div className="stat-label">Average Pitch</div>
          <div className="stat-value">{Math.round(stats.averagePitch)} Hz</div>
          <div className="stat-detail">{getPitchDescription(stats.averagePitch)}</div>
          <div className="stat-range">
            Range: {Math.round(stats.pitchRange.min)} - {Math.round(stats.pitchRange.max)} Hz
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🔊</div>
          <div className="stat-label">Average Volume</div>
          <div className="stat-value">{stats.averageVolume}%</div>
          <div className="stat-detail">{getVolumeDescription(stats.averageVolume)}</div>
          <div className="stat-range">
            Range: {stats.volumeRange.min}% - {stats.volumeRange.max}%
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">💬</div>
          <div className="stat-label">Speaking Rate</div>
          <div className="stat-value">{stats.estimatedWordsPerMinute} WPM</div>
          <div className="stat-detail">{getSpeakingRateDescription(stats.estimatedWordsPerMinute)}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🤫</div>
          <div className="stat-label">Silence Ratio</div>
          <div className="stat-value">{Math.round(stats.silenceRatio * 100)}%</div>
          <div className="stat-detail">
            {stats.silenceRatio > 0.3 ? 'Many pauses' : 
             stats.silenceRatio > 0.15 ? 'Normal pausing' : 
             'Continuous speech'}
          </div>
        </div>
      </div>
      
      <div className="analysis-summary">
        <h3>Summary</h3>
        <p>
          Your recording lasted {formatDuration(stats.duration)} with an average pitch of {Math.round(stats.averagePitch)} Hz 
          ({getPitchDescription(stats.averagePitch).toLowerCase()} range). 
          You spoke at approximately {stats.estimatedWordsPerMinute} words per minute, 
          which is considered {getSpeakingRateDescription(stats.estimatedWordsPerMinute).toLowerCase()}. 
          Your volume was {getVolumeDescription(stats.averageVolume).toLowerCase()} with {stats.pauseCount} natural pauses.
        </p>
      </div>
      
      <DiagnosticData audioStats={stats} transcription={stats.transcription} />
      
      <PracticeRecommendations phonemeStats={stats.phonemeStats} />
    </div>
  );
};

export default VoiceStats;